import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Pull a human-readable message out of a failed API call, matching
 * the `(e as {response?:{data?:{detail?:string}}})?.response?.data
 * ?.detail ?? (e as {message?:string})?.message ?? fallback` unwrap
 * repeated across the admin surfaces (RecordingSettingsCard,
 * UploadPolicyCard, ...) — centralized here so the new auth pages
 * (LoginPage, InviteAcceptPage, TeamPage) share one
 * implementation instead of re-deriving it. Special-cases 429 (rate
 * limit): the backend's detail there is slowapi's own default string
 * ("N per M second"), not copy written for end users.
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 429) return 'Too many attempts — wait a minute and try again.';
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  const message = (err as { message?: string })?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

/** '127.0 GB'-style byte formatter shared by the fleet surfaces. */
export function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  return `${v.toFixed(1)} ${units[u]}`;
}

/**
 * Operator convention: a vehicle field of "." (or any
 * punctuation-only value) means INTENTIONALLY BLANK — the enrollment
 * form requires something, so a dot is typed to skip it. Normalize at
 * display time (storage untouched) so no surface renders ". · idle".
 * Returns null for blank/placeholder values.
 */
export function displayVehicle(vehicle: string | null | undefined): string | null {
  const v = (vehicle ?? '').trim();
  if (v === '' || /^[\p{P}\p{S}]+$/u.test(v)) return null;
  return v;
}

/**
 * Battery level arrives in TWO units depending on the sender: some
 * heartbeat paths report a 0–1 fraction, others (persisted
 * fleet_devices.battery served by /map/devices) a 0–100 percent —
 * the backend forwards `battery.level` verbatim either way. The UI
 * assumes fraction and multiplies by 100, so a percent-unit source
 * rendered "9300%". Normalize every ingestion point to a 0–1 fraction:
 * values above 1 can only be percent. (A literal 1 is read as 100%,
 * not 1% — the fraction interpretation wins the ambiguity.)
 */
export function normalizeBatteryFraction(
  level: number | null | undefined,
): number | null {
  if (level == null || !Number.isFinite(level)) return null;
  const frac = level > 1 ? level / 100 : level;
  return Math.max(0, Math.min(1, frac));
}

/**
 * Backend timestamps (e.g. DeviceOut.last_seen_at) are NAIVE UTC
 * isoformat — no 'Z'/offset — so bare Date.parse reads them in the
 * BROWSER's timezone and every age computed from them is off by the
 * UTC offset (UTC+3 viewers: ages go 3 h negative → Lag forever
 * "fresh", Last seen forever "just now"). Append 'Z' when no offset
 * is present. Same trap as the backend's naive-ts rule in
 * circuit_engine/refine._telemetry_inputs.
 */
export function parseUtcMs(iso: string): number {
  return Date.parse(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
}

/**
 * Coarse "X ago" for last-seen/heartbeat readouts — terse per the
 * design language ("short over long"). One canonical copy: Fleet rows,
 * fleet cards and DeviceDetail all show the same string for the same
 * device.
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = parseUtcMs(iso);
  if (!Number.isFinite(t)) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}
